import fb from '@/firebaseConfig.js';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  onSnapshot,
  updateDoc,
  collection,
  deleteField,
  query,
  where,
  getDocs,
  doc
} from 'firebase/firestore';

import debounce from 'lodash/debounce';
import { reactive, toRef, ref, computed, set } from '@vue/composition-api';

const userSummary = reactive({
  docId: null,
  contexts: {},
  collaborators: {},
  folders: {},
  loading: false,
  error: null
});

const subscription = ref(null);

export default function useUserSummary() {
  const subscribe = async () => {
    if (!userSummary.loading) {
      const auth = getAuth();
      const db = getFirestore();
      console.log('subscribe to user summary');
      userSummary.loading = true;
      const currentUser = auth.currentUser;
      const userId = currentUser.uid;
      const userSummaryQuery = query(
        collection(db, 'accessSummary'),
        where('userId', '==', userId)
      );

      //  Get intial data
      try {
        const snapshot = await getDocs(userSummaryQuery);
        if (snapshot.empty) {
          console.log('No matching documents');
          //  NEED TO SET A TIMEOUT HERE!!!
        }
        snapshot.forEach(doc => {
          const docData = doc.data();
          //update the context information for each context
          Object.values(docData.contextUpdates).forEach(c => {
            if (!c.toDelete) {
              const authors = c.authorIds.map(a => doc.data().collaborators[a]);
              const folderName = c.folderId ? doc.data().folders[c.folderId].name : 'unsorted';
              const context = {
                ...c,
                authors,
                folderName,
                lastEdited: c.lastEdited ? c.lastEdited.toDate() : c.lastEdited,
                lastOpened: c.lastOpened ? c.lastOpened.toDate() : c.lastOpened
              };
              set(userSummary.contexts, context.id, context);
            }
          });

          //update collaborators folders and the id
          userSummary.collaborators = docData.collaborators;
          userSummary.folders = docData.folders;
          userSummary.docId = doc.id;
        });
      } catch (e) {
        userSummary.error = e;
        throw new Error(e);
      }

      console.log(userSummary);
      userSummary.loading = false;
      // Setup subscription
      subscription.value = onSnapshot(userSummaryQuery, querySnapshot => {
        querySnapshot.forEach(doc => {
          console.log('new data from user summary subscription', doc);
          const source = doc.metadata.hasPendingWrites ? 'Local' : 'Server';
          if (source == 'Server') {
            const docData = doc.data();
            Object.values(docData.contextUpdates).forEach(c => {
              const authors = c.authorIds.map(a => doc.data().collaborators[a]);
              const folderName = c.folderId ? doc.data().folders[c.folderId].name : 'unsorted';
              const context = {
                ...c,
                folderName,
                authors,
                lastEdited: c.lastEdited ? c.lastEdited.toDate() : c.lastEdited,
                lastOpened: c.lastOpened ? c.lastOpened.toDate() : c.lastOpened
              };
              set(userSummary.contexts, context.id, context);
              // userSummary.contexts[context.id] = context;
            });
            const incomingContextKeys = Object.keys(docData.contextUpdates);
            const currentContextKeys = Object.keys(userSummary.contexts);
            currentContextKeys.forEach(contextId => {
              if (!incomingContextKeys.includes(contextId)) {
                console.log(`key ${contextId} is no longer included marking it toDelte`);
                set(userSummary.contexts[contextId], 'toDelete', true);
              }
            });
            userSummary.collaborators = doc.data().collaborators;
            userSummary.folders = doc.data().folders;
            userSummary.docId = doc.id;
          }
        });
      });
      //if it is loading then run a function to wait for it to finish
    } else {
      const waitForLoad = () => {
        console.log('wait for load');
        return new Promise(resolve => {
          setTimeout(() => {
            if (userSummary.loading) {
              resolve(waitForLoad());
            } else {
              resolve('userSummary was finished loading');
            }
          }, 100);
        });
      };
      const result = await waitForLoad();
      console.log('subscription result', result);
    }
    console.log('userSummary.docId in subscription', userSummary.docId);
    return;
  };

  const unsubscribe = clear => {
    subscription.value();
    if (clear) {
      userSummary.docId = null;
      userSummary.contexts = {};
      userSummary.collaborators = {};
      userSummary.folders = {};
    }
  };

  //  Fetch Contexts
  const filterContexts = ({ limit, sortBy, sortOrder = 'desc', filterFunc = false }) => {
    return computed(() => {
      console.log('run filterContexts', filterFunc);
      let contexts = Object.values(userSummary.contexts).filter(e => !e.toDelete);
      if (filterFunc) {
        contexts = contexts.filter(filterFunc);
      }
      if (sortBy) {
        contexts = contexts.sort((a, b) => {
          const r = sortOrder == 'desc' ? -1 : 1;
          return b[sortBy] < a[sortBy] ? r : -r;
        });
      }
      if (limit) {
        contexts = contexts.slice(0, limit);
      }
      return contexts;
    });
  };

  const fetchContext = contextId => {
    return computed(() => {
      return userSummary.contexts[contextId];
    });
  };

  const sortedFolders = computed(() => {
    return Object.values(userSummary.folders).sort((a, b) => (a.name < b.name ? -1 : 1));
  });

  const sortedCollaborators = computed(() => {
    return Object.values(userSummary.collaborators).sort((a, b) =>
      a.collaborations < b.collaborations ? -1 : 1
    );
  });

  const updateContextKey = ({ contextId, key, value }) => {
    const prev = userSummary.contexts[contextId][key];
    userSummary.contexts[contextId][key] = value;
    const update = {};
    update[`contextUpdates.${contextId}.${key}`] = value;
    const docRef = doc(collection(db, 'accessSummary', userSummary.docId));
    updateDoc(docRef, update).catch(e => {
      console.log(e);
      userSummary.contexts[contextId][key] = prev;
    });
  };

  //  Update contexts
  const updateFolderName = ({ folderId, value }) => {
    const prev = userSummary.folders[folderId].name;
    set(userSummary.folders[folderId], 'name', value);
    console.log(userSummary.folders[folderId]);
    const update = {};
    update[`folders.${folderId}.name`] = value;
    debounceWriteDb({ docId: userSummary.docId, update });
  };

  const addContext = async data => {
    const authors = data.authorIds.map(a => userSummary.collaborators[a]);
    const folderName = data.folderId ? userSummary.folders[data.folderId].name : 'unsorted';
    set(userSummary.contexts, data.id, { ...data, authors, folderName });
    const update = {};
    update[`contextUpdates.${data.id}`] = data;
    //  check if userSummary exists, if not wait for it
    if (!userSummary.docId) {
      let run = 0;
      const waitForUserSummary = () => {
        const max = 100;
        console.log('wait for user summary docId');
        return new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            if (userSummary.docId || run == max) {
              resolve();
              clearInterval(interval);
            } else {
              console.log('wait for user summary');
              run++;
            }
          }, 200);
        });
      };
      await waitForUserSummary();
      console.log('got past the waitForUserSummary');
    }
    console.log('add context', userSummary.docId, update, { ...userSummary });
    const db = getFirestore();
    const docRef = doc(collection(db, 'accessSummary', userSummary.docId));
    updateDoc(docRef, update)
      .catch(e => {
        console.log(e);
        // userSummary.folders[folderId].name = prev;
      })
      .then(() => true);
  };

  const deleteFolder = folderId => {
    delete userSummary.folders[folderId];
    //  Move any contexts in this folder to unsorted
    const update = {};
    update[`folders.${folderId}`] = deleteField();
    const docRef = doc(collection(db, 'accessSummary', userSummary.docId));
    updateDoc(docRef, update).catch(e => {
      console.log(e);
      // userSummary.folders[folderId].name = prev;
    });
  };

  const addFolder = folderName => {
    const newDocRef = doc(collection(db, 'accessSummary'));
    const folderId = newDocRef.id;
    console.log(folderId);
    set(userSummary.folders, folderId, { name: folderName, id: folderId });
    const update = {};
    update[`folders.${folderId}`] = { name: folderName, id: folderId };
    const docRef = doc(collection(db, 'accessSummary', userSummary.docId));
    updateDoc(docRef, update).catch(e => {
      console.log(e);
      // userSummary.folders[folderId].name = prev;
    });
  };

  const changeContextFolder = ({ contextId, folderId }) => {
    set(userSummary.contexts[contextId], 'folderId', folderId);
    set(userSummary.contexts[contextId], 'folderName', userSummary.folders[folderId].name);
    const update = {};
    update[`contextUpdates.${contextId}.folderId`] = folderId;
    const docRef = doc(collection(db, 'accessSummary', userSummary.docId));
    updateDoc(docRef, update).catch(e => {
      console.log(e);
      // userSummary.folders[folderId].name = prev;
    });
  };

  const debounceWriteDb = debounce(({ docId, update }) => {
    const docRef = doc(collection(db, 'accessSummary', docId));
    updateDoc(docRef, update).catch(e => {
      console.log(e);
      // userSummary.folders[folderId].name = prev;
    });
  }, 1000);

  return {
    subscribe,
    unsubscribe,
    allContexts: toRef(userSummary, 'contexts'),
    filterContexts,
    fetchContext,
    updateContextKey,
    folders: toRef(userSummary, 'folders'),
    sortedFolders,
    loading: toRef(userSummary, 'loading'),
    sortedCollaborators,
    updateFolderName,
    addContext,
    addFolder,
    changeContextFolder,
    docId: computed(() => userSummary.docId)
  };
}
